import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface AdvancedSettingsSectionProps {
  anchorIdPrefix: string;
  hasLockedSetting: boolean;
  children: ReactNode;
}

/**
 * Collapsible "Advanced settings" card, closed by default. It opens when an ERP locks
 * one of its settings or when settings search jumps to a row whose id starts with the prefix.
 * @param props - AdvancedSettingsSectionProps
 * @returns Advanced settings section element
 */
export function AdvancedSettingsSection({
  anchorIdPrefix,
  hasLockedSetting,
  children,
}: AdvancedSettingsSectionProps) {
  const location = useLocation();
  const isSearchTarget = location.hash.startsWith(`#${anchorIdPrefix}`);
  const [isOpen, setIsOpen] = useState(hasLockedSetting || isSearchTarget);

  useEffect(() => {
    if (hasLockedSetting || isSearchTarget) {
      setIsOpen(true);
    }
  }, [hasLockedSetting, isSearchTarget, location.key]);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-6">
      <h3 id={anchorIdPrefix} className="pl-2 scroll-mt-24">
        <CollapsibleTrigger className="flex items-center gap-1 text-lg font-semibold tracking-tight">
          Advanced settings
          <ChevronRight
            className={cn(
              "h-4 w-4 text-muted-foreground transition-transform",
              isOpen && "rotate-90",
            )}
            aria-hidden
          />
        </CollapsibleTrigger>
      </h3>
      <CollapsibleContent className="animate-collapsible-down data-[state=closed]:animate-collapsible-up">
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">{children}</div>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}
